import cx from 'classnames';
import { canUseDOM } from 'exenv';
import { t } from 'i18next';
import { Trans } from 'react-i18next';
import _ from 'lodash';
import moment from 'moment';
import qs from 'qs';
import Menu, { MenuItem, SubMenu } from 'rc-menu';
import { Component, ReactElement, ReactNode, useEffect, useMemo, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';
import { compose } from 'redux';
import { Expansions, FeatureFlagMap, LoggedEntityStatus } from '../../../../../../shared/common/enums';
import { formatPhaseDisplay, getPhaseProgress } from '../../../../../../shared/common/bossPhases';
import { getBestPrecision } from '../../../../../../shared/common/utils';
import { TVideoTrackType } from '../../../../../../shared/common/videoUtils';
import { DarkModeIconTooltip } from '../../../../../../shared/frontend/components/darkModeIconTooltip';
import { Button } from '../../../../../../shared/frontend/components/presentational/buttons/Button';
import Skeleton from '../../../../../../shared/frontend/components/presentational/skeleton';
import { Spinner } from '../../../../../../shared/frontend/components/presentational/spinner';
import { Stack } from '../../../../../../shared/frontend/components/stack/Stack';
import {
  areFieldsDifferent,
  dictToHashKeys,
  formatMsShort,
  formatMsShortNoHours,
  getHashVars,
  naturalDate,
  naturalMonthAndDayAndYear,
  relativeDate,
  trackLinkingToVideos
} from '../../../../../../shared/frontend/libs/utils';
import { TError, createActionSetError } from '../../../../../../shared/frontend/scripts/actions/errorActions';
import { useDisclosure } from '../../../../../../shared/frontend/scripts/hooks/useDisclosure';
import { useBodyScroll } from '../../../../../../shared/frontend/scripts/hooks/useWidgetBodyScrolling';
import { useFeature } from '../../../../../../shared/frontend/scripts/selectors/global';
import { asset } from '../../../../../../shared/frontend/services/assetResolverService';
import { FeatureFlag } from '../../../../../../shared/gameConfig/sharedTypes';
import { RouteComponentProps } from '../../../../../../shared/common/types/common';
import { IGetGroupCompositionCompareItem } from '../../../../shared/types/raidTypes';
import { withSiteMeta } from '../../../../../../shared/frontend/HOCS/withSiteMeta';
import GuildActionCreator from '../../actions/guildActionCreator';
import { namedRoutes, resolveRoutePath } from '../../bundles/routes';
import frontendLog from '../../libs/frontendLog';
import { TApplicationState } from '../../libs/store';
import { GroupCompositionModal } from '../../raiding/components/EditGroupCompositionModal';
import { EncounterKillRosterTable } from '../../raiding/components/EncounterKillRosterTable';
import {
  getModifiedArtifactTraits,
  getRosterSummaries,
  shouldShowArtifactTraits,
  shouldShowAzeriteGear,
  shouldShowDominationItems,
  shouldShowRaidGraphs,
  shouldShowSepulcherTierSummary,
  sortRoster
} from '../../raiding/utils';
import apiService, { TGetGuildBossKillDetails, apiServiceAsync } from '../../services/apiService';
import EntityVideosPanel from '../../shared/videos/EntityVideosPanel';
import { LoadableHealthOverAttemptWidget } from '../../streamWidget/containers/loadableHealthOverAttempt';
import { Link, TrackedLink } from '../../../../../../shared/frontend/components/link';
import { NumberOrNotAvailable } from '../presentational/numberOrNotAvailable';
import { ComplexTooltip, SimpleTooltip } from '../../../../../../shared/frontend/components/presentational/tooltip';
import { CharacterRosterName } from './characterRosterName';
import { BossAttempts } from './guildBossAttempts';
import { LoggedSourceLine } from './loggedSourceLine';
import { VantusTooltipContent } from './vantusTooltipContent';
import EntityAddVideo from '../../../../../../shared/frontend/scripts/videos/components/EntityAddVideo';
import { ModerationReportButton } from '../../../../../../shared/frontend/components/moderation/ModerationReportButton';
import { ModerationReportContextType } from '../../../../../../shared/common/moderationTypes';
import CombatLogDownloadIcon from '../combatlog/CombatLogDownloadModal';
import { isCombatLogDownloadRaidAllowed } from '../../../../shared/combatLogDownloadAccess';

type TPropsFromState = {
  details: any;
  isLoading: boolean;
  forceRefresh: boolean;
  ui: any;
  bossKillKey: string;
  raid?: { difficulty?: string };
};

type TPropsThatDispatch = {
  setGuildBossKillDetails(key: string, details: any): void;
  setGuildBossKillForceRefresh(key: string): void;
  showLoadingSpinner(key: string, show: boolean): void;
  showError(actionData: TError): void;
};

type TOwnProps = {
  raid: any;
  hidePermalink?: boolean; // only show permalink option if embedding
  bossKillHeaderVariant?: TBossKillHeaderVariant;
  flushHorizontalPadding?: boolean;
  enableRosterRowHover?: boolean;
  withHash?: boolean;
  history: any;
  // Logged Page
  loggedPage?: boolean;
  loggedEncounterId?: number;

  // Guild Page
  boss?: any;
  difficulty?: any;
  guild?: any;
};

const SEPULCHER_TIER = '28';

export type TBossKillHeaderVariant = 'full' | 'compact' | 'none';

export type TGuildDetailsProps = TPropsFromState & TPropsThatDispatch & RouteComponentProps<any, any>;

type TEncounterBreakdownEvent = { eventDate: number; text: ReactElement };

function refreshWowheadTooltips() {
  if (canUseDOM && typeof rioDebouncedRefreshWowheadTooltips === 'function') {
    rioDebouncedRefreshWowheadTooltips();
  }
}

const LoggedTick = withSiteMeta((props: any) => {
  let icon = <></>;
  let message = '';

  switch (props.status) {
    case LoggedEntityStatus.STARTED:
      message = t('mplus-runs:logged-status.started');
      icon = <i className="fa fa-spinner fa-spin" style={{ color: '#ffbd0a' }} />;
      break;
    case LoggedEntityStatus.FINISHED:
      message = t('mplus-runs:logged-status.finished');
      icon = <i className="fa fa-badge-check" style={{ color: '#ffbd0a' }} />;
      break;
    case LoggedEntityStatus.ABORTED:
      message = t('mplus-runs:logged-status.aborted');
      icon = <i className="fa fa-times" style={{ color: '#ffbd0a' }} />;
      break;
  }

  const isAdmin = props.auth && (props.auth.features || []).includes(FeatureFlag.LIVE_RAID_ALL);

  let fullIcon = icon;

  if (isAdmin) {
    fullIcon = (
      <Link to={resolveRoutePath(namedRoutes.RaidLoggedEncounter, { raid: props.raid, id: props.loggedEncounterId })}>
        {icon}
      </Link>
    );
  }

  const loggedSourceDiv = props.loggedSources && (
    <div className="slds-m-top--x-small">
      {props.loggedSources.map((s: any, index: number) => {
        return <LoggedSourceLine key={index} loggedSource={s} />;
      })}
    </div>
  );

  return (
    <span className="slds-grid slds-grid--vertical slds-grid--align-center">
      <ComplexTooltip
        tooltip={
          <div>
            {message}
            {loggedSourceDiv}
          </div>
        }
        placement="right"
      >
        {fullIcon}
      </ComplexTooltip>

      {isCombatLogDownloadRaidAllowed(props.raid?.slug || props.raid) && (
        <CombatLogDownloadIcon
          type="raid"
          correlationId={props.correlationId}
          subtitle={`${props.difficulty || ''} ${props.boss?.name || props.raid || ''}`.trim()}
        />
      )}
    </span>
  );
});

// bonusesByTier types.
type TierBonuses = { [key: number]: number };

export type TBonusesByTier = { [key: string]: TierBonuses };

type TBossKillHeaderTab = {
  isAttemptView: boolean;
  activePane: string;
  setActivePane: (pane: string) => void;
  currentFeatures: FeatureFlagMap;
  hasHealthPercent: boolean;
  style?: any;
};

function BossKillHeaderTab(props: TBossKillHeaderTab) {
  return (
    <Menu
      onClick={(event: any) => props.setActivePane(event.key)}
      mode="horizontal"
      disabledOverflow={true}
      className="rio-tabbed-menu slds-m-left--x-small"
      style={props.style}
    >
      {!props.isAttemptView && (
        <MenuItem className={cx({ 'rc-menu-item-selected': props.activePane === 'roster' })} key="roster">
          {t('encounter-details:tabs.raid-roster')}
        </MenuItem>
      )}
      {/* Hide the health-over-attempt graphs when the boss has no tracked health %. */}
      {props.hasHealthPercent && (
        <SubMenu
          className={cx({
            'rc-menu-item-selected': ['health_by_pull_count', 'health_by_pull_date', 'health_by_time_spent'].includes(
              props.activePane
            )
          })}
          title={
            <>
              <i className={'fa fa-chart-line slds-m-right--x-small'} />
              {t('encounter-details:tabs.encounter-graphs')}
            </>
          }
          onTitleClick={() => props.setActivePane('health_by_pull_count')}
        >
          <MenuItem key="health_by_pull_count">{t('encounter-details:tabs.encounter-progress-by-pull-count')}</MenuItem>
          <MenuItem key="health_by_pull_date">{t('encounter-details:tabs.encounter-progress-by-pull-date')}</MenuItem>
          <MenuItem key="health_by_time_spent">{t('encounter-details:tabs.encounter-progress-by-time-spent')}</MenuItem>
        </SubMenu>
      )}
      <MenuItem className={cx({ 'rc-menu-item-selected': props.activePane === 'attempts' })} key="attempts">
        <i className={'fa fa-list slds-m-right--x-small'} />
        {t('encounter-details:tabs.recent-pulls')}
      </MenuItem>
      {props.currentFeatures.encounter_breakdown && !props.isAttemptView && (
        <MenuItem
          className={cx({ 'rc-menu-item-selected': props.activePane === 'encounter_breakdown' })}
          key="encounter_breakdown"
        >
          <i className={'fa fa-list slds-m-right--x-small'} />
          {t('encounter-details:tabs.encounter-breakdown')}
        </MenuItem>
      )}
    </Menu>
  );
}

// TODO: move this to the RAID metadata
function getTierForRaid(raid: any) {
  if (raid.expansion === Expansions.Shadowlands) {
    return 28;
  } else if (raid.slug === 'vault-of-the-incarnates') {
    return 29;
  } else if (raid.slug === 'aberrus-the-shadowed-crucible') {
    return 30;
  } else if (raid.slug === 'amirdrassil-the-dreams-hope') {
    return 31;
  } else if (raid.slug === 'nerubar-palace') {
    return 32;
  } else if (raid.slug === 'liberation-of-undermine') {
    return 33;
  } else if (raid.slug === 'manaforge-omega') {
    return 34;
  } else if (raid.slug === 'tier-mn-1') {
    return 35;
  } else if (raid.slug === 'the-venomous-abyss' || raid.slug === 'the-tidebound-grotto') {
    return 36;
  }
  return 0;
}

function renderStatusMessage(status: LoggedEntityStatus, kill: any, loggedPage: boolean) {
  if (status === LoggedEntityStatus.FINISHED) {
    const date = relativeDate(kill.defeatedAt);
    const naturalDateValue = naturalDate(kill.defeatedAt);
    const hasDuration = !!kill.durationMs;
    const duration = hasDuration ? formatMsShortNoHours(kill.durationMs) : '';

    if (kill.isSuccess) {
      if (loggedPage) {
        if (hasDuration) {
          return (
            <Trans i18nKey="encounter-details:header.defeated-with-date-and-duration" values={{ date, duration }}>
              <SimpleTooltip tag="span" tooltip={naturalDateValue} className="text-underline" />
            </Trans>
          );
        } else {
          return (
            <Trans i18nKey="encounter-details:header.defeated-with-date" values={{ date }}>
              <SimpleTooltip tag="span" tooltip={naturalDateValue} className="text-underline" />
            </Trans>
          );
        }
      } else {
        if (hasDuration) {
          return (
            <Trans i18nKey="encounter-details:header.first-defeated-with-date-and-duration" values={{ date, duration }}>
              <SimpleTooltip tag="span" tooltip={naturalDateValue} className="text-underline" />
            </Trans>
          );
        } else {
          return (
            <Trans i18nKey="encounter-details:header.first-defeated-with-date" values={{ date }}>
              <SimpleTooltip tag="span" tooltip={naturalDateValue} className="text-underline" />
            </Trans>
          );
        }
      }
    } else {
      if (hasDuration) {
        return (
          <Trans i18nKey="encounter-details:header.wiped-with-date-and-duration" values={{ date, duration }}>
            <SimpleTooltip tag="span" tooltip={naturalDateValue} className="text-underline" />
          </Trans>
        );
      } else {
        return (
          <Trans i18nKey="encounter-details:header.wiped-with-date" values={{ date }}>
            <SimpleTooltip tag="span" tooltip={naturalDateValue} className="text-underline" />
          </Trans>
        );
      }
    }
  }

  if (status === LoggedEntityStatus.STARTED) {
    const date = relativeDate(kill.pulledAt);
    const naturalDateValue = naturalDate(kill.pulledAt);
    return (
      <>
        <i className="fa fa-spinner fa-spin" /> &nbsp;
        <Trans i18nKey="encounter-details:header.started-with-date" values={{ date }}>
          <SimpleTooltip tag="span" tooltip={naturalDateValue} className="text-underline" />
        </Trans>
      </>
    );
  }

  if (status === LoggedEntityStatus.ABORTED) {
    const date = relativeDate(kill.pulledAt);
    const naturalDateValue = naturalDate(kill.pulledAt);
    return (
      <>
        <i className="fa fa-ban" /> &nbsp;
        <Trans i18nKey="encounter-details:header.abandoned-started-with-date" values={{ date }}>
          <SimpleTooltip tag="span" tooltip={naturalDateValue} className="text-underline" />
        </Trans>
      </>
    );
  }

  return null;
}

function getBossKillPermalink(props: any) {
  if (props.loggedPage) {
    return resolveRoutePath(namedRoutes.RaidLoggedEncounter, {
      raid: props.raid.slug,
      id: props.loggedEncounterId
    });
  }

  return resolveRoutePath(namedRoutes.GuildRaidComposition, {
    region: props.guild.region.slug,
    realm: props.guild.realm.slug,
    guild: props.guild.name,
    difficulty: props.difficulty,
    raid: props.raid.slug,
    boss: props.boss.slug
  });
}

type TBossKillHighlightVariant = Exclude<TBossKillHeaderVariant, 'none'>;

type TBossKillHighlightProps = {
  variant: TBossKillHighlightVariant;
  iconClassName?: string;
  iconSrc?: string;
  iconHref?: string;
  label: ReactNode;
  value: ReactNode;
  tooltip?: ReactNode;
  tooltipPlacement?: 'top' | 'bottom';
  tooltipType?: 'complex' | 'simple';
};

const FULL_BOSS_KILL_HIGHLIGHT_CLASS =
  'slds-m-top--medium slds-size--1-of-2 slds-small-size--1-of-4 slds-medium-size--1-of-7 slds-large-size--1-of-7 slds-text-align--center';

function BossKillHighlight(props: TBossKillHighlightProps) {
  const isCompact = props.variant === 'compact';
  const iconClassName = cx(
    'icon-role slds-avatar rio-border--light rio-shadow--small',
    isCompact ? 'icon-small slds-m-right--x-small' : 'icon-medium slds-m-bottom--x-small',
    props.iconClassName
  );
  const icon = props.iconSrc ? (
    <img loading="lazy" className={iconClassName} src={props.iconSrc} />
  ) : (
    <span className={iconClassName} />
  );
  const linkedIcon = props.iconHref ? <TrackedLink href={props.iconHref}>{icon}</TrackedLink> : icon;
  const content = (
    <span className={cx({ 'rio-boss-kill-header--compact__stat-content': isCompact })}>
      {linkedIcon}
      {isCompact ? (
        <span className="slds-text-align--left">
          <span className="slds-text-heading--small">{props.value}</span>
          <br />
          <span className="slds-text-body--small text-muted">{props.label}</span>
        </span>
      ) : (
        <>
          <br />
          <span className="slds-text-heading--medium">{props.value}</span>
          <br />
          <span className="slds-text-body--small text-muted">{props.label}</span>
        </>
      )}
    </span>
  );

  let renderedContent = content;
  if (props.tooltip) {
    renderedContent =
      props.tooltipType === 'simple' ? (
        <SimpleTooltip placement={props.tooltipPlacement} tooltip={props.tooltip}>
          {content}
        </SimpleTooltip>
      ) : (
        <ComplexTooltip placement={props.tooltipPlacement} tooltip={props.tooltip}>
          {content}
        </ComplexTooltip>
      );
  }

  return (
    <div className={isCompact ? 'rio-boss-kill-header--compact__stat' : FULL_BOSS_KILL_HIGHLIGHT_CLASS}>
      {renderedContent}
    </div>
  );
}

function BossKillHighlights(props: any & { variant: TBossKillHighlightVariant }) {
  const sortedRoster = [...(props.roster || [])].sort(sortRoster);
  const { summaries, validTiers, bonusesByTier } = getRosterSummaries(sortedRoster);
  const tier = getTierForRaid(props.raid);
  const twoPieces = (bonusesByTier?.[tier]?.[2] || 0) + (bonusesByTier?.[tier]?.[3] || 0);
  const validTierBonuses = bonusesByTier[tier]
    ? _.compact([
        bonusesByTier[tier][4] > 0
          ? `${bonusesByTier[tier][4]} ⨉ ${t('mplus-runs:set-pieces-abbreviated', { count: 4 })}`
          : null,
        twoPieces > 0 ? `${twoPieces} ⨉ ${t('mplus-runs:set-pieces-abbreviated', { count: 2 })}` : null
      ])
    : [];

  return (
    <div
      className={
        props.variant === 'compact'
          ? 'rio-boss-kill-header--compact__stats'
          : 'slds-grid slds-wrap slds-grid--align-center slds-grid--align-space'
      }
    >
      <BossKillHighlight
        variant={props.variant}
        iconClassName="item-level"
        label={t('mplus-runs:callouts.item-level')}
        value={<NumberOrNotAvailable num={summaries.itemLevelEquipped.toFixed(2)} />}
      />

      {shouldShowDominationItems(props.raid.slug) && summaries.validShards > 0 && (
        <BossKillHighlight
          variant={props.variant}
          iconClassName="shards"
          label={t('encounter-details:labels.shards-equipped')}
          value={<NumberOrNotAvailable num={summaries.validShards} />}
          tooltip={
            <div>
              {Object.keys(summaries.shards)
                .sort()
                .map((key: string) => (
                  <div key={key} className="slds-text-align--left">
                    {summaries.shards[key]} &times; {key}
                  </div>
                ))}
            </div>
          }
        />
      )}

      {shouldShowSepulcherTierSummary(props.raid.slug) && validTiers.includes(SEPULCHER_TIER) && (
        <BossKillHighlight
          variant={props.variant}
          iconClassName="tier-sepulcher"
          label={t('encounter-details:labels.tier-pieces')}
          value={validTierBonuses.length > 0 ? validTierBonuses.join(', ') : t('encounter-details:labels.no-bonuses')}
          tooltip={
            <div>
              {[4, 3, 2, 1].map((pieceCount) => (
                <div key={pieceCount}>
                  {bonusesByTier[SEPULCHER_TIER][pieceCount] > 0 ? (
                    <span>
                      <b>{t('encounter-details:tier-bonuses.pieces', { count: pieceCount })}</b>{' '}
                      {bonusesByTier[SEPULCHER_TIER][pieceCount]}{' '}
                      {t('encounter-details:tier-bonuses.player', {
                        count: bonusesByTier[SEPULCHER_TIER][pieceCount]
                      })}
                    </span>
                  ) : (
                    ''
                  )}
                </div>
              ))}
            </div>
          }
        />
      )}

      {shouldShowAzeriteGear(props.raid.slug) && (
        <BossKillHighlight
          variant={props.variant}
          iconClassName="heart-of-azeroth"
          label={t('encounter-details:labels.heart-level')}
          value={<NumberOrNotAvailable num={summaries.heartLevel.toFixed(1)} />}
        />
      )}

      {shouldShowArtifactTraits(props.raid.slug) && (
        <BossKillHighlight
          variant={props.variant}
          iconClassName="artifact-power"
          label={t('mplus-runs:callouts.artifact-traits')}
          value={
            <NumberOrNotAvailable
              num={getModifiedArtifactTraits(props.raid.slug, summaries.artifactTraits).toFixed(1)}
            />
          }
        />
      )}

      <BossKillHighlight
        variant={props.variant}
        iconClassName={summaries.rangedDps > summaries.meleeDps ? 'dps-ranged' : 'dps-melee'}
        label={t('common:roles.dps.name')}
        value={summaries.dps}
        tooltip={
          <span>
            {summaries.meleeDps} {t('common:roles.melee.base_name')}
            <br />
            {summaries.rangedDps} {t('common:roles.ranged.base_name')}
          </span>
        }
        tooltipPlacement="bottom"
        tooltipType="simple"
      />

      <BossKillHighlight
        variant={props.variant}
        iconClassName="healer"
        label={t('common:roles.healer.name', { count: 100 })}
        value={summaries.healing}
      />

      <BossKillHighlight
        variant={props.variant}
        iconClassName="tank"
        label={t('common:roles.tank.name', { count: 100 })}
        value={summaries.tank}
      />

      {props.log?.deaths && (
        <BossKillHighlight
          variant={props.variant}
          iconClassName="death"
          label={t('mplus-runs:callouts.total-deaths')}
          value={props.log.deaths.count}
        />
      )}

      {props.log?.vantus?.count > 0 && (
        <BossKillHighlight
          variant={props.variant}
          iconSrc={asset(`/images/wow/icons/medium/${props.log.vantus.spell.icon}.jpg`)}
          iconHref={`https://wowhead.com/spell=${props.log.vantus.spell.id}`}
          label={t('encounter-details:labels.vantus-runes')}
          value={props.log.vantus.count}
          tooltip={<VantusTooltipContent roster={props.roster} />}
          tooltipPlacement="top"
        />
      )}
    </div>
  );
}

function BossKillCompactHeader(props: any) {
  const location = useLocation();
  const queryParams = qs.parse(location.search, { ignoreQueryPrefix: true });
  const permalinkTo = getBossKillPermalink(props);

  return (
    <div
      className={cx('rio-background--header rio-boss-kill-header--compact', {
        'slds-p-horizontal--xx-small': !props.flushHorizontalPadding
      })}
      style={{
        backgroundImage:
          'linear-gradient(rgba(0, 0, 0, 0.72), rgba(0, 0, 0, 0.72)),' +
          `url(${asset(`/images/${props.raid.slug}/headers/_zone.jpg`)})`
      }}
    >
      <div className="slds-grid slds-grid--align-spread slds-grid--vertical-align-start">
        <div className="slds-col">
          {props.log?.id && (
            <LoggedTick
              status={props.status}
              raid={props.raid.slug}
              loggedEncounterId={props.log.id}
              correlationId={props.log.correlationId}
              boss={props.boss}
              difficulty={props.difficulty}
              loggedSources={props.log.sources || []}
            />
          )}
        </div>

        <div className="slds-col">
          {(!props.hidePermalink || queryParams.embed) && (
            <Link
              to={permalinkTo}
              title={t('mplus-runs:permalink')}
              className="mythic-plus-share rio-realm-link slds-text-body--small slds-m-left--small d-inline-block"
            >
              {queryParams.embed ? (
                <img src={asset('/images/brand/Logo_White.svg')} style={{ height: 15 }} />
              ) : (
                <span>
                  {t('mplus-runs:permalink')}
                  <i className="fa fa-link slds-m-left--xx-small" />
                </span>
              )}
            </Link>
          )}
        </div>
      </div>

      <BossKillHighlights {...props} variant="compact" />
    </div>
  );
}

function BossKillFullHeader(props: any) {
  const isAttemptView = props.isAttemptView;
  const hasLoggedDetails = !!props.loggedDetails;
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const sortedRoster = (props.roster || []).sort(sortRoster);

  const permalinkTo = getBossKillPermalink(props);

  const canManageVideos = !isAttemptView && props.canManageOwnVideos;

  const queryParams = qs.parse(location.search, { ignoreQueryPrefix: true });

  const headerImageClassAttempt =
    'slds-m-top--medium slds-size--1-of-2 slds-small-size--1-of-3 slds-medium-size--1-of-3 slds-large-size--1-of-3 slds-text-align--center';

  let bestPercent = null;
  if (isAttemptView && props.attempt.bestPercent !== null) {
    bestPercent = `${getBestPrecision(props.attempt.bestPercent)}%`;
    const phaseProgress = getPhaseProgress(props.difficulty, props.boss.slug, {
      overallPercent: props.attempt.bestPercent / 100,
      elapsedMs: props.attempt.bestAttemptDurationMs
    });
    if (phaseProgress) {
      bestPercent = formatPhaseDisplay(phaseProgress, (bossPercent) => `${getBestPrecision(bossPercent * 100)}%`);
    } else if (props.attempt.progressDisplay) {
      bestPercent = props.attempt.progressDisplay;
    }
  }

  const shareRaidUntil = props.guildPrivacy?.shareRaidUntil;
  const isOnDarkMode = shareRaidUntil ? moment.utc(shareRaidUntil).isBefore(moment.utc()) : false;

  // TODO: test it
  const setGuildBossKillVideo = (key: string, video: any) => {
    if (props.withHash) {
      const newVars = { ...getHashVars(), vod: video.id };

      navigate(location.pathname + '#' + dictToHashKeys(newVars));
    }
    trackLinkingToVideos(TVideoTrackType.Raid, video.id);
    dispatch(GuildActionCreator.setGuildBossKillVideo(key, video));
  };

  return (
    <div>
      <div
        className={cx('rio-background--header slds-p-top--xx-small', {
          'slds-p-horizontal--xx-small': !props.flushHorizontalPadding
        })}
        style={{
          position: 'relative',
          backgroundImage:
            'linear-gradient(rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.7)),' +
            `url(${asset(`/images/${props.raid.slug}/headers/md/${props.boss.slug}.jpg`)})`,
          borderTop: '2px solid #151314',
          borderLeft: '2px solid #151314',
          borderRight: '2px solid #151314'
        }}
      >
        <div className="slds-grid slds-grid--align-spread">
          <div className="slds-col">
            {props.log && props.log.id && (
              <div style={{ fontSize: '1rem' }}>
                <LoggedTick
                  status={props.status}
                  raid={props.raid.slug}
                  loggedEncounterId={props.log.id}
                  correlationId={props.log.correlationId}
                  boss={props.boss}
                  difficulty={props.difficulty}
                  loggedSources={props.log.sources || []}
                />
              </div>
            )}
          </div>

          <div className="slds-col">
            {!queryParams.embed && canManageVideos && (
              <EntityAddVideo
                type="raid"
                onSubmit={(params) =>
                  apiServiceAsync.addRaidEncounterVideo({
                    ...{
                      raid: props.raid.slug,
                      boss: props.boss.slug,
                      guildId: props.guild.id,
                      difficulty: props.difficulty
                    },
                    ...params
                  })
                }
                className="d-inline-block"
                rosters={sortedRoster}
                forceRefresh={() => props.setGuildBossKillForceRefresh(props.bossKillKey)}
              />
            )}

            {(!props.hidePermalink || queryParams.embed) && (
              <Link
                to={permalinkTo}
                title={t('mplus-runs:permalink')}
                className="mythic-plus-share rio-realm-link slds-text-body--small slds-m-left--small d-inline-block"
              >
                {queryParams.embed ? (
                  <img src={asset('/images/brand/Logo_White.svg')} style={{ height: 15 }} />
                ) : (
                  <span>
                    {t('mplus-runs:permalink')}
                    <i className="fa fa-link slds-m-left--xx-small" />
                  </span>
                )}
              </Link>
            )}
          </div>
        </div>

        <div className="slds-m-bottom--x-small slds-text-align--center">
          <div className="slds-text-heading--large">
            {props.guild && props.guild.name && (
              <>
                <b>&lt;{props.guild.displayName}&gt;</b> {t('encounter-details:header.versus')}{' '}
              </>
            )}
            <b>{t(`raids:${props.raid.slug}.bosses.${props.boss.slug}.name`)}</b>
          </div>
          <div className="slds-text-heading--medium">
            {t('encounter-details:header.difficulty-raid-progress', {
              difficulty: t(`common:difficulty.${props.raid.difficulty}.name`)
            })}
          </div>
        </div>
        <div className="slds-text-body--medium text-muted slds-text-align--center slds-m-bottom--large">
          {renderStatusMessage(props.status, props.kill, props.loggedPage)}
        </div>
        {isAttemptView ? (
          <div className="slds-grid slds-wrap slds-grid--align-center slds-grid--align-space">
            {props.attempt.bestPercent !== null && (
              <div className={headerImageClassAttempt}>
                <span className="icon-role icon-medium best-pull slds-avatar slds-m-bottom--x-small rio-border--light rio-shadow--small" />
                <br />
                <Stack style={{ display: 'inline-flex' }} alignItems="center">
                  <span className="slds-text-heading--medium">{bestPercent}</span>
                  {isOnDarkMode && <DarkModeIconTooltip shareRaidUntil={shareRaidUntil} />}
                </Stack>
                <br />
                <span className="slds-text-body--small text-muted">{t('encounter-details:labels.best-pull')}</span>
              </div>
            )}
            {props.attempt.pullCount !== null && (
              <div className={headerImageClassAttempt}>
                <span className="icon-role icon-medium pull-count slds-avatar slds-m-bottom--x-small rio-border--light rio-shadow--small" />
                <br />
                <Stack style={{ display: 'inline-flex' }} alignItems="center">
                  <span className="slds-text-heading--medium">
                    <NumberOrNotAvailable num={props.attempt.pullCount} />
                  </span>
                  {isOnDarkMode && <DarkModeIconTooltip shareRaidUntil={shareRaidUntil} />}
                </Stack>
                <br />
                <span className="slds-text-body--small text-muted">{t('encounter-details:labels.pull-count')}</span>
              </div>
            )}
            <div className={headerImageClassAttempt}>
              <span className="icon-role icon-medium first-pull slds-avatar slds-m-bottom--x-small rio-border--light rio-shadow--small" />
              <br />
              <Stack style={{ display: 'inline-flex' }} alignItems="center">
                <span className="slds-text-heading--small">
                  <SimpleTooltip tooltip={naturalDate(props.attempt.pullStartedAt)}>
                    {naturalMonthAndDayAndYear(props.attempt.pullStartedAt)}
                  </SimpleTooltip>
                </span>
              </Stack>
              <br />
              <span className="slds-text-body--small text-muted">{t('encounter-details:labels.first-pull')}</span>
            </div>
          </div>
        ) : (
          <BossKillHighlights {...props} variant="full" />
        )}
        <Stack justifyContent="space-between" alignItems="flex-end">
          <div className="slds-p-top--large slds-text-align--left" id="rio-encounter-tabs" style={{ flex: 1 }}>
            <div className="slds-grid slds-grid--vertical-align-end" style={{ gap: '8px' }}>
              {!queryParams.embed &&
              !queryParams.embedmode &&
              (hasLoggedDetails || isAttemptView) &&
              shouldShowRaidGraphs(props.raid.slug, props.guildPrivacy) ? (
                <BossKillHeaderTab
                  isAttemptView={isAttemptView}
                  activePane={props.activePane}
                  setActivePane={props.setActivePane}
                  currentFeatures={props.currentFeatures}
                  hasHealthPercent={props.hasHealthPercent}
                  style={{ position: 'relative', top: 5, zIndex: 'auto' }}
                />
              ) : (
                <div />
              )}
              {!queryParams.embed && <CompareButton {...props} />}
            </div>
          </div>

          {!queryParams.embed && (props.kill?.id || props.log?.id) && (
            <ModerationReportButton
              contextType={ModerationReportContextType.RAID_ENCOUNTER}
              entityId={props.kill?.id || props.log?.id}
              sourceUrl={permalinkTo}
              variant="icon"
              linkStyle={true}
            />
          )}
        </Stack>
      </div>
      {!queryParams.embed && props.activePane === 'roster' && (
        <EntityVideosPanel
          canDeleteSelectedVideo={canManageVideos}
          details={props}
          videos={props.videos}
          handleVideoSelect={(video: any) => setGuildBossKillVideo(props.bossKillKey, video)}
          handleRemoveVideo={async (videoId: number) => {
            await apiServiceAsync.removeRaidEncounterVideo(videoId);
            props.setGuildBossKillForceRefresh(props.bossKillKey);
          }}
        />
      )}
    </div>
  );
}

export function BossKillHeader(props: any) {
  const variant: TBossKillHeaderVariant = props.bossKillHeaderVariant || 'full';

  if (variant === 'none') {
    return null;
  }

  if (variant === 'compact') {
    return <BossKillCompactHeader {...props} />;
  }

  return <BossKillFullHeader {...props} />;
}

function CompareButton(props: any) {
  const disclosure = useDisclosure();
  const navigate = useNavigate();
  const isEnabled = useFeature(FeatureFlag.RAID_COMPARISON_BUTTON);

  if (!isEnabled) {
    return null;
  }

  const values: IGetGroupCompositionCompareItem = {
    difficulty: props.difficulty,
    boss: props.boss.slug,
    raid: props.raid.slug,
    name: '',
    realm: '',
    region: ''
  };

  const handleSubmit = (submittedValue: IGetGroupCompositionCompareItem) => {
    const items = [
      { ...values, name: props.guild.name, realm: props.guild.realm.slug, region: props.guild.region.slug },
      submittedValue
    ];

    navigate({
      pathname: namedRoutes.GroupCompositionCompare,
      search: qs.stringify({ items })
    });
  };

  return (
    <>
      <Button
        className="text-bold slds-m-bottom--x-small"
        variant="outlined"
        icon={<i className="fa-solid fa-code-compare" />}
        onClick={disclosure.open}
      >
        {t('composition-compare:compare-raid-comp')}
      </Button>

      <GroupCompositionModal
        isOpen={disclosure.isOpen}
        onClose={disclosure.close}
        onSubmit={handleSubmit}
        saveButtonProps={{
          icon: <i className="fa-solid fa-code-compare" />,
          children: t('composition-compare:compare-button')
        }}
        value={values}
        showPullSelect={false}
      />
    </>
  );
}

function BossKillGraphPane(props: any) {
  return <div style={{ backgroundColor: '#1a1716' }}>{props.children}</div>;
}

function BossKillDetails(props: any) {
  const isAttemptView = !!props.attempt;
  const bossKillHeaderVariant: TBossKillHeaderVariant = props.bossKillHeaderVariant || 'full';
  const location = useLocation();

  // Hide the health-over-attempt graphs when we know the boss has no tracked health %.
  // The attempt view exposes this via a null best percent; the kill view carries no such
  // signal, so we keep showing the tab there (the graph simply renders empty if untracked).
  const hasHealthPercent: boolean = !isAttemptView || props.attempt.bestPercent !== null;

  const [isSettledActivePane, setIsSettledActivePane] = useState(false);
  const [activePane, setActivePane] = useState(
    isAttemptView ? (hasHealthPercent ? 'health_by_pull_count' : 'attempts') : 'roster'
  );

  const sortedRoster = useMemo(() => [...(props.roster || [])].sort(sortRoster), [props.roster]);
  const { summaries, validTiers } = useMemo(() => getRosterSummaries(sortedRoster), [sortedRoster]);

  useEffect(() => {
    if (typeof props.attempt === 'object' && !isSettledActivePane) {
      setActivePane(hasHealthPercent ? 'health_by_pull_count' : 'attempts');
      setIsSettledActivePane(true);
    }
  }, [props.attempt]);

  const queryParams = qs.parse(location.search, { ignoreQueryPrefix: true });

  if (queryParams.embed) {
    useBodyScroll();
  }

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const setGuildBossKillVideo = (key: string, video: any) => {
    if (props.withHash) {
      const newVars = { ...getHashVars(), vod: video.id };

      navigate(location.pathname + '#' + dictToHashKeys(newVars));
    }
    trackLinkingToVideos(TVideoTrackType.Raid, video.id);
    dispatch(GuildActionCreator.setGuildBossKillVideo(key, video));
  };

  const canShowHeader = !queryParams.embedmode || queryParams.embedmode === 'summary';
  // FIXME: remove embedmode=details when we are sure it isn't being used anywhere
  const canShowRoster =
    !queryParams.embedmode || queryParams.embedmode === 'roster' || queryParams.embedmode === 'details';

  if (!props.roster && !isAttemptView) {
    return (
      <div>
        <div style={{ marginTop: 24 }}>
          {bossKillHeaderVariant !== 'none' && (
            <Skeleton height={bossKillHeaderVariant === 'compact' ? 88 : 238} style={{ position: 'relative' }}>
              <Spinner />
            </Skeleton>
          )}

          <div>
            <Skeleton height={36} />
            <Skeleton variant="item" height={841} style={{ position: 'relative' }}>
              <Spinner />
            </Skeleton>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={cx('slds-text-align--center', props.className)}>
      {canShowHeader && (
        <BossKillHeader
          {...props}
          bossKillHeaderVariant={bossKillHeaderVariant}
          activePane={activePane}
          setActivePane={setActivePane}
          isAttemptView={isAttemptView}
          hasHealthPercent={hasHealthPercent}
        />
      )}
      {activePane === 'roster' && canShowRoster && (
        <EncounterKillRosterTable
          raid={props.raid}
          boss={props.boss}
          difficulty={props.difficulty}
          guild={props.guild}
          roster={props.roster}
          videos={props.videos}
          isLoading={props.isLoading}
          summaries={summaries}
          validTiers={validTiers}
          embed={!!queryParams.embed}
          enableRowHover={props.enableRosterRowHover}
          bossKillKey={props.bossKillKey}
          setGuildBossKillVideo={setGuildBossKillVideo}
        />
      )}
      {activePane === 'health_by_pull_count' && (
        <BossKillGraphPane>
          <div style={{ height: 400, width: '100%' }} className="slds-p-around--small">
            <LoadableHealthOverAttemptWidget
              id={`${props.raid.slug}-${props.difficulty}-${props.boss.slug}`}
              showPullAnnotations={false}
              height={350}
              hideTitle={true}
              queryOverride={`?raid=${props.raid.slug}&difficulty=${props.difficulty}&guilds=${encodeURIComponent(
                [props.guild.region.slug, props.guild.realm.slug, props.guild.name].join('/')
              )}&boss=${props.boss.slug}&refresh=0&hideLogo=1&type=attempt`}
            />
          </div>
        </BossKillGraphPane>
      )}
      {activePane === 'health_by_pull_date' && (
        <BossKillGraphPane>
          <div style={{ height: 400, width: '100%' }} className="slds-p-around--small">
            <LoadableHealthOverAttemptWidget
              id={`${props.raid.slug}-${props.difficulty}-${props.boss.slug}`}
              showPullAnnotations={false}
              height={350}
              hideTitle={true}
              queryOverride={`?raid=${props.raid.slug}&difficulty=${props.difficulty}&guilds=${encodeURIComponent(
                [props.guild.region.slug, props.guild.realm.slug, props.guild.name].join('/')
              )}&boss=${props.boss.slug}&refresh=0&hideLogo=1&type=absoluteTime`}
            />
          </div>
        </BossKillGraphPane>
      )}
      {activePane === 'health_by_time_spent' && (
        <BossKillGraphPane>
          <div style={{ height: 400, width: '100%' }} className="slds-p-around--small">
            <LoadableHealthOverAttemptWidget
              id={`${props.raid.slug}-${props.difficulty}-${props.boss.slug}`}
              showPullAnnotations={false}
              height={350}
              hideTitle={true}
              queryOverride={`?raid=${props.raid.slug}&difficulty=${props.difficulty}&guilds=${encodeURIComponent(
                [props.guild.region.slug, props.guild.realm.slug, props.guild.name].join('/')
              )}&boss=${props.boss.slug}&refresh=0&hideLogo=1&type=relativeTime`}
            />
          </div>
        </BossKillGraphPane>
      )}
      {activePane === 'encounter_breakdown' && <BossKillEncounterBreakdown {...props} />}
      {activePane === 'attempts' && <BossAttempts {...props} />}
    </div>
  );
}

function BossKillEncounterBreakdown(encounter: any) {
  const events: TEncounterBreakdownEvent[] = [];

  if (encounter.loggedDetails.deaths) {
    encounter.loggedDetails.deaths.forEach((death: any) => {
      const player = encounter.roster.find((member: any) => member.character.id === death.character_id);

      events.push({
        eventDate: death.relativeDiedAt,
        text: player ? (
          <Trans i18nKey="mplus-runs:event-breakdown.died">
            <CharacterRosterName character={player.character} />
          </Trans>
        ) : (
          <Trans i18nKey="mplus-runs:event-breakdown.died">
            <b>{t('mplus-runs:event-breakdown.player')}</b>
          </Trans>
        )
      });
    });
  }

  events.sort((a, b) => a.eventDate - b.eventDate);

  if (events.length === 0) {
    return <></>;
  }

  return (
    <div className="rio-content-pane slds-text-align--left" style={{ backgroundColor: '#1a1716' }}>
      <div className="rio-run-breakdown slds-m-top--medium slds-text-body--large">
        {events.map((event: TEncounterBreakdownEvent, index: number) => {
          return (
            <div key={index} className="slds-grid slds-p-horizontal--small slds-p-vertical--xx-small">
              <div className="slds-col slds-grow-none text-muted">{formatMsShort(event.eventDate)}</div>
              <div className="slds-col slds-p-left--small">&nbsp;{event.text}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

class BossTooltipContentBase extends Component<any, any> {
  public UNSAFE_componentWillReceiveProps(props: any) {
    if (!props.isLoading && props.ui) {
      refreshWowheadTooltips();
    }
  }

  private interval: NodeJS.Timeout | null = null;

  public componentDidMount() {
    if (this.interval) {
      clearInterval(this.interval);
      this.interval = null;
    }

    this.checkInterval();
  }

  public componentDidUpdate() {
    this.checkInterval();
  }

  private checkInterval() {
    // Only start refreshing if the status is STARTED and the interval is not already set
    if (this.props.status === LoggedEntityStatus.STARTED && !this.interval) {
      this.interval = setInterval(() => {
        if (this.props.bossKillKey) {
          this.props.setGuildBossKillForceRefresh(this.props.bossKillKey);
        }
      }, 30 * 1000);
    } else {
      if (this.interval) {
        clearInterval(this.interval);
        this.interval = null;
      }
    }
  }

  public componentWillUnmount() {
    if (this.interval) {
      clearInterval(this.interval);
      this.interval = null;
    }
  }

  public UNSAFE_componentWillMount() {
    refreshWowheadTooltips();
  }

  public render(): ReactElement<TGuildDetailsProps> {
    return <BossKillDetails {...this.props} />;
  }
}

function generateKey(ownProps: TOwnProps) {
  if (ownProps.loggedPage) {
    return `logged-${ownProps.loggedEncounterId}`;
  } else {
    return `${ownProps.raid.slug}-${ownProps.boss.slug}-${ownProps.difficulty}`;
  }
}

// take state from store and convert into props
function mapStateToProps(state: TApplicationState, ownProps: TOwnProps): TPropsFromState {
  const key = generateKey(ownProps);
  let selectedVideo: any = null;

  const videos = (state as any).guildDetails.bossKills[key]?.videos;
  if (videos) {
    if (ownProps.withHash) {
      const hashVars = getHashVars();
      selectedVideo = hashVars.vod
        ? (state as any).guildDetails.bossKills[key].videos.find((video: any) => video.id.toString() === hashVars.vod)
        : null;
      if (selectedVideo) {
        selectedVideo.autoPlay = true;
      }
    }

    if (!selectedVideo) {
      // choose a random one with a consistent seed
      selectedVideo = videos[(state.siteConfig.seed * Number.MAX_SAFE_INTEGER) % videos.length];
    }
  }

  return {
    ...((state as any).guildDetails.bossKills ? { selectedVideo, ...(state as any).guildDetails.bossKills[key] } : {}),
    isLoading: (state as any).guildDetails.loadingBossKills?.[key] || false,
    forceRefresh: (state as any).guildDetails.forceRefreshBossKills
      ? (state as any).guildDetails.forceRefreshBossKills[key]
      : false,
    currentFeatures: state.currentFeatures,
    bossKillKey: key,
    // isAbleToManageVideos,
    auth: (state as any).auth
  } as any;
}

// return props that need access to `dispatch` function (props that can modify the store?)
function mapDispatchToProps(dispatch: Function, ownProps: TOwnProps): TPropsThatDispatch {
  return {
    setGuildBossKillDetails(key: string, details: any) {
      dispatch(GuildActionCreator.setGuildBossKillDetails(key, details));
    },
    setGuildBossKillForceRefresh(key: string) {
      dispatch(GuildActionCreator.setGuildBossKillForceRefresh(key));
    },
    showError(actionData: TError) {
      dispatch(createActionSetError(actionData));
    },
    showLoadingSpinner(key: string, show: boolean) {
      dispatch(GuildActionCreator.showGuildBossKillDetailsLoadingSpinner(key, show));
    }
  };
}

// return one entire set of props
function mergeProps(stateProps: TPropsFromState, dispatchProps: TPropsThatDispatch, ownProps: TOwnProps) {
  let loadParams: TGetGuildBossKillDetails | any;
  let checkFields: string[];
  let functionToCall: (params: any, callback: any) => void;

  if (ownProps.loggedPage) {
    functionToCall = apiService.getRaidLoggedEncounterDetails;

    checkFields = ['raid', 'id'];
    loadParams = { raid: ownProps.raid.slug, id: ownProps.loggedEncounterId };
  } else {
    functionToCall = apiService.getGuildBossKillDetails;

    checkFields = ['raid', 'difficulty', 'region', 'realm', 'guild', 'boss'];
    loadParams = {
      raid: ownProps.raid.slug,
      difficulty: ownProps.difficulty,
      region: ownProps.guild.region.slug,
      realm: ownProps.guild.realm.slug,
      guild: ownProps.guild.name,
      boss: ownProps.boss.slug
    };
  }

  const key = generateKey(ownProps);

  const contentChanged = !stateProps.ui || areFieldsDifferent(checkFields, loadParams, stateProps.ui);
  if ((!stateProps.isLoading && contentChanged) || stateProps.forceRefresh) {
    dispatchProps.showLoadingSpinner(key, true);

    functionToCall(loadParams, (err: any, result?: any) => {
      if (err) {
        dispatchProps.showError({ title: t('common:errors.something-went-wrong'), body: err.toString() });
        frontendLog.error({ err }, 'getGuildBossKillDetails failed');
        // dispatchProps.showLoadingSpinner(key, false);
        return;
      }

      if (!result) {
        dispatchProps.showError({
          title: t('common:errors.something-went-wrong'),
          body: t('common:errors.unable-to-load-data-reload')
        });
        frontendLog.error('getGuildBossKillDetails empty');
        // dispatchProps.showLoadingSpinner(key, false);
        return;
      }

      dispatchProps.setGuildBossKillDetails(key, result.killDetails);
    });
  }

  const difficulty = ownProps.difficulty || stateProps.raid?.difficulty;

  return { ...ownProps, ...stateProps, ...dispatchProps, difficulty };
}

export default compose(connect(mapStateToProps, mapDispatchToProps, mergeProps) as any)(BossTooltipContentBase as any);
